'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

// ─── 3D DATA ──────────────────────────────────────────────────────────────────────────────
// Sourced from Behance gallery: https://www.behance.net/gallery/112291665/Monolyth-(VR)
// 3D section — actual 3D renders, architectural visualizations, VR environments
// Images verified from live Behance page (67fbabc1 series)

interface ThreeDItem {
  id: number;
  src: string;
  alt: string;
  title: string;
  renderDescription: string;
  processNotes: string;
  software: string;
  year: string;
  category: 'environment' | 'architecture' | 'panorama' | 'concept' | 'render';
  bridgeLinks: {label: string;href: string;color: string;}[];
}

// Main collage / hero image for the 3D section
const THREED_COLLAGE_SRC =
'https://mir-s3-cdn-cf.behance.net/project_modules/1400_webp/078460112291665.67fbabc562aa1.jpg';

const THREED_ITEMS: ThreeDItem[] = [
{
  id: 1,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/589302112291665.67fbabc17bf5e.jpg',
  alt: 'Monolyth 3D render — architectural visualization with ornate structural elements and atmospheric lighting',
  title: 'Architectural Visualization I',
  renderDescription: 'Architectural visualization featuring ornate structural elements with rich atmospheric lighting. This render explores the intersection of historical architectural forms and digital space, creating environments that feel simultaneously ancient and futuristic.',
  processNotes: 'High-detail architectural modeling with PBR materials. Atmospheric lighting using HDRI environment maps. Post-processing in Photoshop for final color grading.',
  software: 'Blender / Photoshop',
  year: '2021',
  category: 'architecture',
  bridgeLinks: [
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 2,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/b1c8f9112291665.67fbabc177471.jpg',
  alt: 'Monolyth 3D render — complex interior architectural space with layered geometric forms',
  title: 'Interior Architectural Space',
  renderDescription: 'Complex interior architectural space with layered geometric forms and dramatic lighting. The render explores spatial depth and the relationship between structure and atmosphere in virtual environments.',
  processNotes: 'Interior lighting setup with area lights and global illumination. Geometric complexity achieved through modular architectural components. Render time optimized using denoising.',
  software: 'Blender / Substance Painter',
  year: '2021',
  category: 'architecture',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 3,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/23b980112291665.67fbabc18019f.jpg',
  alt: 'Monolyth 3D render — surreal landscape environment with atmospheric depth and organic forms',
  title: 'Surreal Landscape Environment',
  renderDescription: 'Surreal landscape environment with atmospheric depth and organic forms. This render captures the otherworldly quality of the Monolyth open world — familiar yet transformed through the lens of community data visualization.',
  processNotes: 'Terrain generation from heightmap data. Volumetric atmosphere using OpenVDB. Organic forms sculpted in ZBrush and retopologized for real-time use.',
  software: 'Blender / ZBrush / Houdini',
  year: '2021',
  category: 'environment',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 4,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/c035cd112291665.67fbabc1815b9.jpg',
  alt: 'Monolyth 3D render — vertical format environment with towering architectural structures',
  title: 'Vertical Environment — Towers',
  renderDescription: 'Vertical format environment featuring towering architectural structures that evoke both industrial heritage and speculative futures. The scale communicates the weight of community history encoded in the built environment.',
  processNotes: 'Vertical composition designed for VR headset viewing. Modular tower system allows rapid iteration. Procedural weathering materials applied using Substance Designer.',
  software: 'Blender / Substance Designer / Unity',
  year: '2021',
  category: 'architecture',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 5,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/2605d7112291665.67fbabc176b8e.jpg',
  alt: 'Monolyth 3D render — atmospheric environment with warm lighting and organic landscape',
  title: 'Atmospheric Warm Environment',
  renderDescription: 'Atmospheric environment with warm golden lighting and organic landscape forms. The warm palette evokes the golden hour light of the three cities — Pittsburgh, Detroit, Chicago — at their most beautiful and most vulnerable.',
  processNotes: 'HDRI sky with warm sunset tones. Volumetric god rays through atmospheric haze. Landscape sculpted from GIS elevation data with artistic interpretation.',
  software: 'Blender / Houdini / Nuke',
  year: '2021',
  category: 'environment',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' }]

},
{
  id: 6,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/1400/7e22e4112291665.67fbabc18064e.jpg',
  alt: 'Monolyth 3D render — wide format architectural visualization with complex structural geometry',
  title: 'Wide Format Architectural Render',
  renderDescription: 'Wide format architectural visualization with complex structural geometry. The panoramic composition captures the full spatial experience of the Monolyth virtual environment — designed for immersive display.',
  processNotes: 'Wide aspect ratio render for panoramic display. Multiple render passes composited for maximum detail. Final output optimized for VR headset display.',
  software: 'Blender / Nuke / DaVinci Resolve',
  year: '2021',
  category: 'panorama',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' }]

},
{
  id: 7,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/d30aa0112291665.67fbabc17926d.jpg',
  alt: 'Monolyth 3D render — detailed architectural interior with ornate decorative elements',
  title: 'Ornate Interior Architecture',
  renderDescription: 'Detailed architectural interior with ornate decorative elements — a virtual space that draws on the rich architectural heritage of Black community spaces, reimagined through the Monolyth visual language.',
  processNotes: 'High-poly decorative elements baked to low-poly meshes for real-time performance. PBR materials with custom normal maps for surface detail. Interior lighting designed for warmth and safety.',
  software: 'Blender / Substance Painter / Unity',
  year: '2021',
  category: 'architecture',
  bridgeLinks: [
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 8,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/c47c09112291665.67fbabc178916.jpg',
  alt: 'Monolyth 3D render — atmospheric landscape with dramatic sky and organic terrain',
  title: 'Dramatic Sky Landscape',
  renderDescription: 'Atmospheric landscape with dramatic sky and organic terrain — the Monolyth world at its most expansive. The sky communicates the emotional weight of the community histories encoded in the terrain below.',
  processNotes: 'Volumetric cloud system using OpenVDB. Terrain generated from GIS data with artistic sculpting. Atmospheric scattering calibrated for dramatic golden hour quality.',
  software: 'Blender / Houdini / Photoshop',
  year: '2021',
  category: 'environment',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 9,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/6e33ae112291665.67fbabc17df8d.jpg',
  alt: 'Monolyth 3D render — interior space with warm lighting and architectural detail',
  title: 'Warm Interior Space',
  renderDescription: 'Interior space with warm lighting and rich architectural detail — the virtual cultural center at the heart of the Monolyth open world. A gathering space that exists simultaneously in physical memory and virtual space.',
  processNotes: 'Interior lighting using area lights and indirect illumination. Warm color palette achieved through custom HDRI and post-processing. Architectural details reference actual community centers.',
  software: 'Blender / Substance Designer / Unity',
  year: '2021',
  category: 'architecture',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 10,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/9ca057112291665.67fbabc179d5d.jpeg',
  alt: 'Monolyth 3D render — wide format panoramic environment with complex architectural forms',
  title: 'Panoramic Architectural Environment',
  renderDescription: 'Wide format panoramic environment with complex architectural forms — the full spatial experience of the Monolyth world captured in a single frame. Designed for immersive VR display.',
  processNotes: 'Panoramic render using equirectangular camera projection. Multiple render passes composited for maximum detail. Final output optimized for VR headset display.',
  software: 'Blender / Nuke / DaVinci Resolve',
  year: '2021',
  category: 'panorama',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' }]

},
{
  id: 11,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/c72cfb112291665.67fbabc182ebe.jpg',
  alt: 'Monolyth 3D render — detailed environment with rich surface textures and atmospheric depth',
  title: 'Rich Surface Texture Environment',
  renderDescription: 'Detailed environment with rich surface textures and atmospheric depth — the Monolyth visual language at its most material. Every surface tells a story of community history and environmental transformation.',
  processNotes: 'PBR materials with custom texture sets created in Substance Painter. Surface detail achieved through displacement mapping. Atmospheric depth using exponential fog with custom density curves.',
  software: 'Blender / Substance Painter / Photoshop',
  year: '2021',
  category: 'render',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 12,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/bff93d112291665.67fbabc177dc9.jpg',
  alt: 'Monolyth 3D render — vertical format environment with dramatic vertical architectural elements',
  title: 'Vertical Architectural Drama',
  renderDescription: 'Vertical format environment with dramatic vertical architectural elements — the scale of the Monolyth world communicating the weight of industrial history and community resilience.',
  processNotes: 'Vertical composition optimized for mobile VR viewing. Dramatic scale achieved through careful attention to human-scale reference objects. Industrial materials use procedural aging.',
  software: 'Blender / Substance Painter / Unity',
  year: '2021',
  category: 'architecture',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 13,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/e795dd112291665.67fbabc17ed4d.jpg',
  alt: 'Monolyth 3D render — concept environment with abstract geometric forms and atmospheric effects',
  title: 'Abstract Geometric Concept',
  renderDescription: 'Concept environment with abstract geometric forms and atmospheric effects — the Monolyth visual language at its most abstract. Geometry as pure data, form as pure community information.',
  processNotes: 'Abstract geometry generated through algorithmic processes. Atmospheric effects using particle systems and volumetric rendering. Color palette derived from the Haal Project design tokens.',
  software: 'Houdini / Blender / Nuke',
  year: '2021',
  category: 'concept',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' }]

},
{
  id: 14,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/9ca057112291665.67fbabc179d5d.jpeg',
  alt: 'Monolyth 3D render — atmospheric outdoor scene with natural organic landscape',
  title: 'Organic Natural Landscape',
  renderDescription: 'Atmospheric outdoor scene with natural organic landscape — the Monolyth world in its most naturalistic state. The organic forms reference the natural environments that existed before industrial development.',
  processNotes: 'Organic landscape sculpted from GIS data with artistic interpretation. Vegetation using particle systems with custom plant models. Atmospheric scattering for natural light quality.',
  software: 'Blender / Houdini / Photoshop',
  year: '2021',
  category: 'environment',
  bridgeLinks: [
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' },
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]
},
{
  id: 15,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/1400/0775df112291665.67fbabc1836e7.jpg',
  alt: 'Monolyth 3D render — wide format panoramic environment with layered architectural depth',
  title: 'Layered Architectural Depth',
  renderDescription: 'Wide format panoramic environment with layered architectural depth — the Monolyth world at its most complex. Multiple layers of architectural forms create a sense of infinite depth and discovery.',
  processNotes: 'Wide format render with multiple architectural layers. Depth of field used to guide viewer attention. Post-processing in Nuke for final color grading and atmospheric effects.',
  software: 'Blender / Nuke / DaVinci Resolve',
  year: '2021',
  category: 'panorama',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' }]
},
{
  id: 16,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/385f08112291665.67fbabc184eff.jpg',
  alt: 'Monolyth 3D render — detailed environment with complex surface textures and atmospheric lighting',
  title: 'Complex Surface Textures',
  renderDescription: 'Detailed environment with complex surface textures and atmospheric lighting — the Monolyth visual language at its most material. Every surface tells a story of community history.',
  processNotes: 'PBR materials with custom texture sets. Surface detail achieved through displacement mapping. Atmospheric depth using exponential fog.',
  software: 'Blender / Substance Painter / Photoshop',
  year: '2021',
  category: 'render',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]
},
{
  id: 17,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/41a537112291665.67fbabc17a959.jpg',
  alt: 'Monolyth 3D render — surreal architectural concept with floating geometric elements',
  title: 'Floating Geometric Elements',
  renderDescription: 'Surreal architectural concept with floating geometric elements — the Monolyth world defying gravity. The floating forms represent the aspirations of communities that have been weighed down by industrial pollution.',
  processNotes: 'Floating geometry achieved through careful composition and lighting. Particle systems for atmospheric dust and debris. Color palette derived from the Haal Project design tokens.',
  software: 'Houdini / Blender / Nuke',
  year: '2021',
  category: 'concept',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' }]
},
{
  id: 18,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/1400/f3eb4a112291665.67fbabc17f462.jpg',
  alt: 'Monolyth 3D render — wide format environment with dramatic horizontal composition',
  title: 'Dramatic Horizontal Composition',
  renderDescription: 'Wide format environment with dramatic horizontal composition — the Monolyth world stretching to the horizon. The horizontal format captures the vast scale of the industrial landscapes that inspired the project.',
  processNotes: 'Wide aspect ratio render for panoramic display. Multiple render passes composited for maximum detail. Final output optimized for VR headset display.',
  software: 'Blender / Nuke / DaVinci Resolve',
  year: '2021',
  category: 'panorama',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]
},
{
  id: 19,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/c250e2112291665.67fbabc184752.jpg',
  alt: 'Monolyth 3D render — detailed architectural environment with warm atmospheric lighting',
  title: 'Warm Atmospheric Architecture',
  renderDescription: 'Detailed architectural environment with warm atmospheric lighting — the Monolyth world at its most inviting. The warm palette evokes the golden hour light of the three cities at their most beautiful.',
  processNotes: 'HDRI sky with warm sunset tones. Volumetric god rays through atmospheric haze. Architectural details reference actual community centers.',
  software: 'Blender / Substance Designer / Unity',
  year: '2021',
  category: 'architecture',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]
},
{
  id: 20,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/c858b1112291665.67fbabc183f12.jpg',
  alt: 'Monolyth 3D render — vertical format environment with towering organic structures',
  title: 'Towering Organic Structures',
  renderDescription: 'Vertical format environment with towering organic structures — the Monolyth world at its most biological. The organic forms reference the natural environments that existed before industrial development.',
  processNotes: 'Organic structures sculpted in ZBrush and retopologized for real-time use. Vertical composition optimized for mobile VR viewing.',
  software: 'ZBrush / Blender / Unity',
  year: '2021',
  category: 'environment',
  bridgeLinks: [
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]
},
{
  id: 21,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/4920d8112291665.67fbabc180c8f.jpg',
  alt: 'Monolyth 3D render — atmospheric environment with layered depth and organic forms',
  title: 'Layered Depth Environment',
  renderDescription: 'Atmospheric environment with layered depth and organic forms — the Monolyth world at its most immersive. Multiple layers of depth create a sense of infinite exploration.',
  processNotes: 'Multiple render passes composited for maximum depth. Atmospheric scattering calibrated for dramatic quality. Post-processing in Photoshop for final color grading.',
  software: 'Blender / Photoshop / Nuke',
  year: '2021',
  category: 'environment',
  bridgeLinks: [
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' },
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' }]
},
{
  id: 22,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/475bce112291665.67fbabc1821a6.jpg',
  alt: 'Monolyth 3D render — complex architectural visualization with ornate structural elements',
  title: 'Ornate Structural Elements',
  renderDescription: 'Complex architectural visualization with ornate structural elements — the Monolyth world at its most detailed. Every structural element references the rich architectural heritage of Black community spaces.',
  processNotes: 'High-poly decorative elements baked to low-poly meshes. PBR materials with custom normal maps for surface detail. Interior lighting designed for warmth and safety.',
  software: 'Blender / Substance Painter / Unity',
  year: '2021',
  category: 'architecture',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' }]
}];


const CATEGORY_LABELS_3D: Record<ThreeDItem['category'], string> = {
  environment: 'Environment',
  architecture: 'Architecture',
  panorama: 'Panorama',
  concept: 'Concept',
  render: 'Render'
};

const CATEGORY_COLORS_3D: Record<ThreeDItem['category'], string> = {
  environment: '#2d8a87',
  architecture: '#8a8ab8',
  panorama: '#c9a84c',
  concept: '#D4A82A',
  render: '#2d7a47'
};

// ─── LIGHTBOX ─────────────────────────────────────────────────────────────────
interface LightboxProps {
  item: ThreeDItem;
  total: number;
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

function ThreeDLightbox({ item, total, currentIndex, onClose, onPrev, onNext }: LightboxProps) {
  const [tab, setTab] = useState<'render' | 'process'>('render');

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(4,8,12,0.97)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}
      onClick={onClose}>
      
      <div
        style={{
          maxWidth: 1200, width: '96vw', maxHeight: '94vh',
          display: 'flex', flexDirection: 'column',
          background: '#0a1018',
          border: '1px solid rgba(45,138,135,0.2)',
          borderRadius: 4, overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}>
        
        {/* Top bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px',
          borderBottom: '1px solid rgba(45,138,135,0.12)',
          background: '#060d12', flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              fontFamily: 'monospace', fontSize: 8, letterSpacing: '.18em',
              textTransform: 'uppercase', color: 'rgba(45,138,135,0.5)'
            }}>
              ◎ 3D · {String(currentIndex + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </span>
            <span style={{
              fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em',
              textTransform: 'uppercase', color: CATEGORY_COLORS_3D[item.category],
              padding: '2px 7px', border: `1px solid ${CATEGORY_COLORS_3D[item.category]}40`,
              borderRadius: 2
            }}>
              {CATEGORY_LABELS_3D[item.category]}
            </span>
            <span style={{
              fontFamily: 'monospace', fontSize: 7, letterSpacing: '.08em',
              color: 'rgba(212,197,169,0.3)'
            }}>
              {item.software}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid rgba(45,138,135,0.2)',
              color: 'rgba(212,197,169,0.5)', cursor: 'pointer',
              width: 28, height: 28, borderRadius: 3, fontSize: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
            ✕</button>
        </div>

        {/* Image + info */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {/* Image */}
          <div style={{
            flex: '1 1 62%', position: 'relative', background: '#040810',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden'
          }}>
            <button onClick={onPrev} style={{
              position: 'absolute', left: 12, zIndex: 2,
              background: 'rgba(4,8,12,0.7)', border: '1px solid rgba(45,138,135,0.2)',
              color: 'rgba(212,197,169,0.7)', cursor: 'pointer',
              width: 36, height: 36, borderRadius: 3, fontSize: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>‹</button>
            <img
              src={item.src}
              alt={item.alt}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            
            <button onClick={onNext} style={{
              position: 'absolute', right: 12, zIndex: 2,
              background: 'rgba(4,8,12,0.7)', border: '1px solid rgba(45,138,135,0.2)',
              color: 'rgba(212,197,169,0.7)', cursor: 'pointer',
              width: 36, height: 36, borderRadius: 3, fontSize: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>›</button>
          </div>

          {/* Info panel */}
          <div style={{
            flex: '0 0 320px', padding: '20px 18px', overflowY: 'auto',
            borderLeft: '1px solid rgba(45,138,135,0.12)',
            background: '#060d12',
            display: 'flex', flexDirection: 'column', gap: 14
          }}>
            <div>
              <p style={{
                fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em',
                textTransform: 'uppercase', color: 'rgba(45,138,135,0.4)',
                marginBottom: 6
              }}>
                {item.year} · {item.software.split('/')[0].trim()}
              </p>
              <h3 style={{
                fontFamily: "'Amiri', serif", fontSize: 19, color: '#F5F0E8',
                fontWeight: 400, lineHeight: 1.3, marginBottom: 0
              }}>
                {item.title}
              </h3>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4 }}>
              {(['render', 'process'] as const).map((t) =>
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  fontFamily: 'monospace', fontSize: 7, letterSpacing: '.12em',
                  textTransform: 'uppercase', padding: '4px 10px', borderRadius: 2,
                  cursor: 'pointer', transition: 'all 0.15s',
                  border: `1px solid ${tab === t ? 'rgba(45,138,135,0.5)' : 'rgba(45,138,135,0.15)'}`,
                  background: tab === t ? 'rgba(45,138,135,0.1)' : 'transparent',
                  color: tab === t ? '#2d8a87' : 'rgba(212,197,169,0.35)'
                }}>
                
                  {t === 'render' ? '◎ Render' : '⌘ Process'}
                </button>
              )}
            </div>

            <p style={{
              fontFamily: "'Georgia', serif", fontSize: 12.5, color: 'rgba(212,197,169,0.65)',
              lineHeight: 1.75, margin: 0
            }}>
              {tab === 'render' ? item.renderDescription : item.processNotes}
            </p>

            {/* Bridge links */}
            <div>
              <p style={{
                fontFamily: 'monospace', fontSize: 7, letterSpacing: '.15em',
                textTransform: 'uppercase', color: 'rgba(45,138,135,0.35)',
                marginBottom: 8
              }}>
                ◎ Bridge Links
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {item.bridgeLinks.map((link) =>
                <Link
                  key={link.href}
                  href={link.href}
                  style={{
                    fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em',
                    textTransform: 'uppercase', color: link.color,
                    textDecoration: 'none', padding: '5px 10px',
                    border: `1px solid ${link.color}30`,
                    borderRadius: 3, background: `${link.color}08`,
                    display: 'flex', alignItems: 'center', gap: 6
                  }}>
                  
                    <span style={{ opacity: 0.6 }}>→</span> {link.label}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

}

// ─── 3D SECTION ───────────────────────────────────────────────────────────────
export default function ThreeDSection() {
  const [activeFilter, setActiveFilter] = useState<ThreeDItem['category'] | 'all'>('all');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const filtered = activeFilter === 'all' ?
  THREED_ITEMS :
  THREED_ITEMS.filter((item) => item.category === activeFilter);

  const openLightbox = useCallback((idx: number) => setLightboxIndex(idx), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() => {
    setLightboxIndex((i) => i === null ? null : (i - 1 + filtered.length) % filtered.length);
  }, [filtered.length]);
  const nextImage = useCallback(() => {
    setLightboxIndex((i) => i === null ? null : (i + 1) % filtered.length);
  }, [filtered.length]);

  const categories: Array<ThreeDItem['category'] | 'all'> = ['all', 'environment', 'architecture', 'panorama', 'concept', 'render'];

  return (
    <section id="3d" style={{ paddingBottom: 80, borderTop: '1px solid rgba(45,138,135,0.12)' }}>
      {/* Lightbox */}
      {lightboxIndex !== null &&
      <ThreeDLightbox
        item={filtered[lightboxIndex]}
        total={filtered.length}
        currentIndex={lightboxIndex}
        onClose={closeLightbox}
        onPrev={prevImage}
        onNext={nextImage} />

      }

      <div style={{ padding: '60px 7.1% 40px' }}>
        {/* Section header */}
        <div style={{ marginBottom: 40 }}>
          <p style={{
            fontFamily: 'monospace', fontSize: 9, letterSpacing: '.18em',
            textTransform: 'uppercase', color: 'rgba(45,138,135,0.5)', marginBottom: 10
          }}>
            ◎ Portfolio · Segment III
          </p>
          <h2 style={{
            fontFamily: "'Amiri', serif", fontSize: 42, color: '#F5F0E8',
            fontWeight: 400, marginBottom: 14, lineHeight: 1.1
          }}>
            3D
          </h2>
          <p style={{
            fontFamily: "'Georgia', serif", fontSize: 15, color: 'rgba(212,197,169,0.6)',
            lineHeight: 1.8, maxWidth: 680, marginBottom: 24
          }}>
            3D environment design and rendering from the Monolyth (VR) project — virtual landscapes generated from community data, architectural reconstructions of Pittsburgh, Detroit, and Chicago, and the surreal open-world environments that form the immersive backbone of the platform.
          </p>

          {/* Bridge links row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 28 }}>
            {[
            { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
            { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
            { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' },
            { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' }].
            map((link) =>
            <Link
              key={link.href}
              href={link.href}
              style={{
                fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em',
                textTransform: 'uppercase', color: link.color,
                textDecoration: 'none', padding: '5px 12px',
                border: `1px solid ${link.color}30`,
                borderRadius: 3, background: `${link.color}08`
              }}>
              
                → {link.label}
              </Link>
            )}
          </div>
        </div>

        {/* Main collage image */}
        <div style={{ marginBottom: 32, position: 'relative' }}>
          <img
            src={THREED_COLLAGE_SRC}
            alt="Monolyth (VR) — 3D section main collage showing the complete virtual environment system across Pittsburgh, Detroit, and Chicago"
            style={{
              width: '100%', display: 'block',
              borderRadius: 3,
              border: '1px solid rgba(45,138,135,0.12)'
            }} />
          
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            padding: '20px 20px 16px',
            background: 'linear-gradient(to top, rgba(4,8,12,0.9) 0%, transparent 100%)',
            borderRadius: '0 0 3px 3px'
          }}>
            <p style={{
              fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em',
              textTransform: 'uppercase', color: 'rgba(45,138,135,0.6)', margin: 0
            }}>
              ◎ Monolyth (VR) — 3D Environment System · 2018–2021
            </p>
          </div>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
          {categories.map((cat) =>
          <button
            key={cat}
            onClick={() => setActiveFilter(cat)}
            style={{
              fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em',
              textTransform: 'uppercase', padding: '5px 12px', borderRadius: 3,
              cursor: 'pointer', transition: 'all 0.15s',
              border: `1px solid ${activeFilter === cat ? 'rgba(45,138,135,0.5)' : 'rgba(45,138,135,0.15)'}`,
              background: activeFilter === cat ? 'rgba(45,138,135,0.1)' : 'transparent',
              color: activeFilter === cat ? '#2d8a87' : 'rgba(212,197,169,0.4)'
            }}>
            
              {cat === 'all' ?
            `All (${THREED_ITEMS.length})` :
            `${CATEGORY_LABELS_3D[cat as ThreeDItem['category']]} (${THREED_ITEMS.filter((i) => i.category === cat).length})`}
            </button>
          )}
        </div>

        {/* 25-image grid — mixed layout like Behance */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 3
        }}>
          {filtered.map((item, idx) =>
          <div
            key={item.id}
            onClick={() => openLightbox(idx)}
            style={{
              position: 'relative', cursor: 'pointer',
              aspectRatio: '16/10', overflow: 'hidden',
              background: '#040810',
              border: '1px solid rgba(45,138,135,0.08)',
              // Span some items wider for visual variety
              gridColumn: idx === 0 ? 'span 2' : idx === 6 ? 'span 2' : idx === 12 ? 'span 2' : idx === 18 ? 'span 2' : 'span 1'
            }}>
            
              <img
              src={item.src}
              alt={item.alt}
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                transition: 'transform 0.3s ease', display: 'block'
              }}
              onMouseEnter={(e) => {(e.currentTarget as HTMLImageElement).style.transform = 'scale(1.04)';}}
              onMouseLeave={(e) => {(e.currentTarget as HTMLImageElement).style.transform = 'scale(1)';}} />
            
              {/* Hover overlay */}
              <div
              style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to top, rgba(4,8,12,0.88) 0%, transparent 55%)',
                opacity: 0, transition: 'opacity 0.2s',
                display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                padding: '10px 10px 8px'
              }}
              onMouseEnter={(e) => {(e.currentTarget as HTMLDivElement).style.opacity = '1';}}
              onMouseLeave={(e) => {(e.currentTarget as HTMLDivElement).style.opacity = '0';}}>
              
                <p style={{
                fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em',
                textTransform: 'uppercase', color: CATEGORY_COLORS_3D[item.category],
                margin: '0 0 3px'
              }}>
                  {CATEGORY_LABELS_3D[item.category]}
                </p>
                <p style={{
                fontFamily: "'Georgia', serif", fontSize: 11, color: '#F5F0E8',
                margin: 0, lineHeight: 1.3
              }}>
                  {item.title}
                </p>
                <p style={{
                fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)',
                margin: '3px 0 0', letterSpacing: '.06em'
              }}>
                  {item.software.split('/')[0].trim()} · {item.year}
                </p>
              </div>
              {/* Index badge */}
              <div style={{
              position: 'absolute', top: 6, left: 6,
              fontFamily: 'monospace', fontSize: 7, letterSpacing: '.08em',
              color: 'rgba(45,138,135,0.5)',
              background: 'rgba(4,8,12,0.7)',
              padding: '2px 5px', borderRadius: 2
            }}>
                {String(idx + 1).padStart(2, '0')}
              </div>
            </div>
          )}
        </div>

        {/* Count label */}
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <span style={{
            fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em',
            color: 'rgba(45,138,135,0.35)'
          }}>
            {filtered.length} render{filtered.length !== 1 ? 's' : ''} · click to expand · ◎ render description + ⌘ process notes
          </span>
        </div>
      </div>
    </section>);

}